import React from 'react';
import type { LiveStats } from '../api/simStudioApi';
import type { Theme } from './StudioHeader';
import {
  IconUsers, IconCheckCircle, IconUserX,
  IconClipboard, IconBell, IconClock,
} from './Icons';

interface Props {
  stats: LiveStats | null;
  loading: boolean;
  theme: Theme;
}

interface CardDef {
  Icon: React.FC<{ size?: number; color?: string }>;
  label: string;
  accent: string;      // hex
  getValue: (s: LiveStats) => string | number;
  getSub?: (s: LiveStats) => string;
}

const CARDS: CardDef[] = [
  {
    Icon: IconUsers,
    label: 'Total de Alunos',
    accent: '#60a5fa',
    getValue: s => s.totalAlunos,
  },
  {
    Icon: IconCheckCircle,
    label: 'Presença Hoje',
    accent: '#4ade80',
    getValue: s => `${s.presencaPct}%`,
    getSub: s => `${s.presentesHoje} presentes`,
  },
  {
    Icon: IconUserX,
    label: 'Ausências',
    accent: '#f87171',
    getValue: s => s.ausentesHoje,
  },
  {
    Icon: IconClipboard,
    label: 'Chamadas Concluídas',
    accent: '#a78bfa',
    getValue: s => s.chamadasConcluidas,
    getSub: s => `de ${s.chamadasHoje} abertas`,
  },
  {
    Icon: IconBell,
    label: 'Notificações',
    accent: '#fbbf24',
    getValue: s => s.notifHoje,
  },
  {
    Icon: IconClock,
    label: 'Atualizado às',
    accent: '#34d399',
    getValue: s => new Date(s.atualizadoEm).toLocaleTimeString('pt-BR', {
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    }),
  },
];

function hexToRgb(hex: string) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r},${g},${b}`;
}

function StatCard({ card, stats, theme }: { card: CardDef; stats: LiveStats; theme: Theme }) {
  const isDark = theme === 'dark';
  const rgb = hexToRgb(card.accent);

  return (
    <div
      style={{
        background: isDark
          ? `rgba(${rgb}, 0.06)`
          : `rgba(${rgb}, 0.05)`,
        border: `1px solid rgba(${rgb}, ${isDark ? '0.18' : '0.15'})`,
        borderRadius: 14,
        padding: '18px 20px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Faixa decorativa esquerda */}
      <div style={{
        position: 'absolute', left: 0, top: '18%', bottom: '18%',
        width: 3, borderRadius: '0 4px 4px 0',
        background: card.accent, opacity: 0.7,
      }} />

      <div className="flex items-start justify-between gap-3">
        {/* Texto */}
        <div className="flex-1 min-w-0">
          <div
            style={{
              fontSize: 32,
              fontWeight: 900,
              lineHeight: 1,
              letterSpacing: '-0.02em',
              color: isDark ? '#f1f5f9' : '#0f172a',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {card.getValue(stats)}
          </div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              marginTop: 6,
              color: isDark ? '#94a3b8' : '#64748b',
            }}
          >
            {card.label}
          </div>
          {card.getSub && (
            <div style={{ fontSize: 11, marginTop: 3, color: isDark ? '#64748b' : '#94a3b8' }}>
              {card.getSub(stats)}
            </div>
          )}
        </div>

        {/* Ícone monocromático */}
        <div
          style={{
            width: 40, height: 40, borderRadius: 10, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: `rgba(${rgb}, ${isDark ? '0.12' : '0.1'})`,
          }}
        >
          <card.Icon size={20} color={card.accent} />
        </div>
      </div>
    </div>
  );
}

export function StatsGrid({ stats, loading, theme }: Props) {
  const isDark = theme === 'dark';
  const skeletonBg = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)';

  if (loading && !stats) {
    return (
      <div className="grid grid-cols-2 xl:grid-cols-3 gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} style={{ height: 90, borderRadius: 14, background: skeletonBg }} className="animate-pulse" />
        ))}
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="grid grid-cols-2 xl:grid-cols-3 gap-3">
      {CARDS.map(card => (
        <StatCard key={card.label} card={card} stats={stats} theme={theme} />
      ))}
    </div>
  );
}
